import classnames from 'classnames'
import * as d3 from 'd3'
import * as React from 'react'

import { round } from '../../../../../app/backtest/utils'
import { colors } from '../../../../../app/stats/constants'
import { ETFHoldingsBreakdownResponse } from '../types'
import { getKeyFromStrings, getReducedBreakdownData } from '../utils'

interface PieChartProps {
  data: ETFHoldingsBreakdownResponse
  width?: number
  height?: number
  type: string
}

type MappedETFHoldingsData = { value: number; label: string; instrument: string }

export function PieChart({ width = 150, height = 150, data, type }: PieChartProps) {
  const [hovered, setHovered] = React.useState<string | null>(null)
  const reducedBreakdownData = React.useMemo(() => getReducedBreakdownData(data.rowData), [data])

  const radius = Math.min(width, height) / 2

  const pieData = React.useMemo(
    () =>
      reducedBreakdownData
        .flatMap((item) =>
          item.data.map((dataItem) => ({
            label: dataItem.sector,
            value: dataItem.absoluteWeight,
            instrument: item.instrument,
          }))
        )
        .sort((a, b) => b.value - a.value),
    [reducedBreakdownData]
  )

  const color = d3.scaleOrdinal<string>().range(colors)
  const arc = d3.arc<any, d3.PieArcDatum<MappedETFHoldingsData>>().outerRadius(radius).innerRadius(0)
  const pie = d3
    .pie<any, MappedETFHoldingsData>()
    .sort(null)
    .value((d) => d.value)

  const totalSize = pieData.reduce((p, c) => p + c.value, 0)
  const isSomethingHovered = !!hovered

  return (
    <div className="flex grow px-4 pb-4" data-testid={`etf-holdings-${type}`}>
      {totalSize > 0 && (
        <div className="pt-4">
          <svg width={width} height={height}>
            <g transform={`translate(${width / 2},${height / 2})`}>
              {pie(pieData).map((d, i) => (
                <g key={`${d.data.label}-${i}`} className="arc">
                  <path
                    d={arc(d)!}
                    className={classnames({
                      'stroke-white': !FinvizSettings.hasDarkTheme,
                      'stroke-gray-800': FinvizSettings.hasDarkTheme,
                      'opacity-60':
                        isSomethingHovered && hovered !== getKeyFromStrings(d.data.label, d.data.instrument),
                    })}
                    style={{ fill: color(d.data.label) }}
                    onMouseEnter={() => setHovered(getKeyFromStrings(d.data.label, d.data.instrument))}
                    onMouseLeave={() => setHovered(null)}
                  />
                </g>
              ))}
            </g>
          </svg>
        </div>
      )}
      <div className="flex w-full flex-col pl-6 text-2xs">
        {reducedBreakdownData.map((instrumentGroup, index) =>
          instrumentGroup.data.length > 0 ? (
            <React.Fragment key={instrumentGroup.instrument}>
              <div className="flex justify-start text-muted">{instrumentGroup.instrument}</div>
              <div
                className={classnames('grid grid-flow-col', {
                  'mb-4': index === 0,
                })}
                style={{
                  gridTemplateRows: `repeat(${Math.max(Math.ceil(instrumentGroup.itemsToDisplay / 2), 1)}, auto)`,
                }}
              >
                {instrumentGroup.data.map((d) => (
                  <div
                    key={`${instrumentGroup.instrument}-${d.sector}`}
                    className={classnames(
                      {
                        'opacity-60':
                          isSomethingHovered && hovered !== getKeyFromStrings(d.sector, instrumentGroup.instrument),
                      },
                      'flex max-h-5 items-center justify-start pr-3 text-default'
                    )}
                    onMouseEnter={() => setHovered(getKeyFromStrings(d.sector, instrumentGroup.instrument))}
                    onMouseLeave={() => setHovered(null)}
                  >
                    <div className="flex items-center pr-1.5">
                      <div className="h-3 w-3 rounded-full" style={{ backgroundColor: color(d.sector) }} />
                    </div>
                    <div className="min-w-11 pr-1.5 text-right tabular-nums font-medium">{`${
                      totalSize > 0 ? round((100 / totalSize) * d.absoluteWeight, 1) : 0
                    }%`}</div>
                    <div className="whitespace-nowrap">{d.sector}</div>
                  </div>
                ))}
              </div>
            </React.Fragment>
          ) : null
        )}
      </div>
    </div>
  )
}
